/**
 * 複数の BlueBean CSV(OPレポート・対応履歴)をまとめて取り込むための計画と実行。
 *  - ファイルの種類は列名で自動判定(ログインID＋通話回数 → OPレポート／リアクション情報＋登録者ID → 対応履歴)
 *  - 対象日はファイル名(…_2026-9-9.csv)から。読めなければ画面の日付
 *  - 同じ日は OPレポート → 対応履歴 の順に、1ファイル = 1バッチで順番に書く(途中で失敗しても、そのファイルより前の分は残る)
 *  - 同じファイル(同じハッシュ)が取込済みならスキップ。既存の上書き・同じ業務の差し替えは確認のチェックが必要
 */
import { parseCsv, readCsvFile, sha256Hex } from '@core/lib/csv';
import type { Company, DailySummary, DateStr, Kintai, Operator } from '@core/lib/types';
import { aggregateActivity, activityImporter, activityMappingFor, isActivityHeader, type ActivityAggregate } from './activity';
import { buildDailySummary } from './buildDailySummary';
import { fetchBbDailyStats, fetchExistingSummary, fetchImportLogs, fetchKintai, mergeActivity, mergeOpReport, runActivityImport, runOpReportImport, statsToBuildInput, type ImportLog } from './importRepo';
import { DEFAULT_OP_REPORT_MAPPING, dateFromFileName, opReportImporter } from './opReport';
import type { BbDailyStat, DailySummaryBuildResult, ImportIssue, ImportKind, ImportResult, ParsedActivityRow, ParsedOpReportRow, RawRow, UnknownBbIdPolicy } from './types';

export type UploadKind = ImportKind | 'unknown';

/** 列名からファイルの種類を判定する */
export function detectKind(headers: string[]): UploadKind {
  const h = new Set(headers.map((x) => x.trim()));
  if (h.has('ログインID') && h.has('通話回数')) return 'op_report';
  if (isActivityHeader(headers)) return 'activity';
  return 'unknown';
}

export type LoadedFile = {
  id: string;
  name: string;
  hash: string;
  kind: UploadKind;
  date: DateStr;
  rowCount: number;
  op?: ImportResult<ParsedOpReportRow>;
  act?: ImportResult<ParsedActivityRow>;
  /** 列検証・種類不明のエラー */
  headerIssues: ImportIssue[];
};

let seq = 0;

/**
 * CSV 1ファイルを読んで種類を判定し、正規化までする。
 * 対応履歴は「履歴登録日レンジ」を複数日にして出力したファイルも受け付け、発着信時間の日付ごとに分けて返す
 * (1ファイル = 複数日の取込。ハッシュはファイル共通、日付は行から)
 */
export async function loadFile(file: File, fallbackDate: DateStr): Promise<LoadedFile[]> {
  const text = await readCsvFile(file);
  const table = parseCsv(text);
  const hash = await sha256Hex(text);
  const kind = detectKind(table.headers);
  const date = dateFromFileName(file.name) ?? fallbackDate;
  const id = `f${++seq}`;
  const raw: RawRow[] = table.rows.map((cells) => Object.fromEntries(table.headers.map((h, i) => [h, cells[i] ?? ''])));
  const base = { id, name: file.name, hash, kind, date, rowCount: table.rows.length };
  if (kind === 'op_report') {
    const headerIssues = opReportImporter.validateHeader(table.headers, DEFAULT_OP_REPORT_MAPPING);
    const op = headerIssues.some((i) => i.level === 'error') ? undefined : opReportImporter.parse(raw, DEFAULT_OP_REPORT_MAPPING, { name: file.name, hash, rowCount: table.rows.length, date });
    return [{ ...base, op, headerIssues }];
  }
  if (kind === 'activity') {
    // 顧客情報レポートは出力形が2種類ある(列名が「(最新)」付きのものも受け付ける)
    const mapping = activityMappingFor(table.headers);
    const headerIssues = activityImporter.validateHeader(table.headers, mapping);
    if (headerIssues.some((i) => i.level === 'error')) return [{ ...base, headerIssues }];
    const act = activityImporter.parse(raw, mapping, { name: file.name, hash, rowCount: table.rows.length, date });
    const dates = Array.from(new Set(act.rows.map((r) => r.at.slice(0, 10)))).sort();
    if (dates.length <= 1) return [{ ...base, date: dates[0] ?? date, act, headerIssues }];
    // 複数日: 日付ごとに1件ずつ(同じファイル・同じハッシュ)
    return dates.map((d, i) => ({
      ...base,
      id: `${id}_${i}`,
      date: d,
      rowCount: act.rows.filter((r) => r.at.slice(0, 10) === d).length,
      act: { ...act, rows: act.rows.filter((r) => r.at.slice(0, 10) === d), issues: i === 0 ? act.issues : [] },
      headerIssues: i === 0 ? headerIssues : [],
    }));
  }
  return [{ ...base, headerIssues: [{ level: 'error', code: 'unknown_kind', message: `${file.name}: OPレポート(ログインID・通話回数)にも対応履歴(リアクション情報・登録者ID)にも見えません` }] }];
}

export type DateContext = { existing: DailySummary | null; stats: Map<string, BbDailyStat>; opLogs: ImportLog[]; actLogs: ImportLog[]; kintai: Map<string, Kintai> };

export async function loadDateContext(date: DateStr): Promise<DateContext> {
  const [existing, stats, opLogs, actLogs, kintai] = await Promise.all([
    fetchExistingSummary(date),
    fetchBbDailyStats(date),
    fetchImportLogs(date, 'op_report').catch(() => [] as ImportLog[]),
    fetchImportLogs(date, 'activity').catch(() => [] as ImportLog[]),
    fetchKintai(date).catch(() => new Map<string, Kintai>()),
  ]);
  return { existing, stats, opLogs, actLogs, kintai };
}

/** new=新規 / overwrite=既存の日次を上書き / replace=同じ業務の対応履歴を差し替え / skip_same=同じファイルが取込済み / error=読めない */
export type FileStatus = 'new' | 'overwrite' | 'replace' | 'skip_same' | 'error';
export const FILE_STATUS_LABELS: Record<FileStatus, string> = { new: '新規', overwrite: '上書き', replace: '差し替え', skip_same: '取込済み(スキップ)', error: 'エラー' };

export type PlannedFile = {
  file: LoadedFile;
  status: FileStatus;
  agg?: ActivityAggregate;
  issues: ImportIssue[];
  /** 表に出す内容の要約 */
  summary: string;
};

export type DatePlan = {
  date: DateStr;
  files: PlannedFile[];
  build: DailySummaryBuildResult | null;
  buildIssues: ImportIssue[];
};

const kindOrder = (k: UploadKind) => (k === 'op_report' ? 0 : k === 'activity' ? 1 : 2);

/** 取込の計画: 日ごとに OPレポート → 対応履歴 の順で並べ、状態と取込後の日次【集計】を試算する */
export function planUploads(files: LoadedFile[], ctx: Map<DateStr, DateContext>, deps: { companies: Company[]; operators: Operator[]; policy: UnknownBbIdPolicy }): DatePlan[] {
  const byDate = new Map<DateStr, LoadedFile[]>();
  for (const f of files) byDate.set(f.date, [...(byDate.get(f.date) ?? []), f]);
  const seenHash = new Set<string>();
  const plans: DatePlan[] = [];
  for (const [date, list] of [...byDate.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    const c = ctx.get(date);
    const ordered = [...list].sort((a, b) => kindOrder(a.kind) - kindOrder(b.kind));
    let stats = c?.stats ?? new Map<string, BbDailyStat>();
    let hasOp = !!c?.existing;
    let activeCodes = new Set((c?.existing?.sources?.activity ?? []).flatMap((s) => s.business_codes));
    const planned: PlannedFile[] = [];
    for (const f of ordered) {
      const issues = [...f.headerIssues];
      if (f.kind === 'unknown' || (!f.op && !f.act)) {
        planned.push({ file: f, status: 'error', issues, summary: '' });
        continue;
      }
      const known = c?.opLogs.concat(c.actLogs).find((l) => l.hash === f.hash);
      // 同じファイルは日付ごとに1回(複数日をまとめたファイルは日付ごとに別扱い)
      const hashKey = `${f.hash}|${date}`;
      if (known || seenHash.has(hashKey)) {
        planned.push({ file: f, status: 'skip_same', issues, summary: known ? `同じファイルを取込済み` : '同じファイルが重複' });
        continue;
      }
      seenHash.add(hashKey);
      if (f.op) {
        issues.push(...f.op.issues);
        const calls = f.op.rows.reduce((s, r) => s + (r.talk_count ?? 0), 0);
        planned.push({ file: f, status: hasOp ? 'overwrite' : 'new', issues, summary: `BB ID ${f.op.rows.length}件・通話回数 ${calls.toLocaleString('ja-JP')}` });
        stats = mergeOpReport(stats, date, f.op.rows, 'preview', f.name);
        hasOp = true;
      } else if (f.act) {
        issues.push(...f.act.issues);
        const agg = aggregateActivity(f.act.rows, { date, companies: deps.companies });
        issues.push(...agg.issues);
        const replace = agg.business_codes.some((code) => activeCodes.has(code));
        planned.push({
          file: f,
          status: replace ? 'replace' : 'new',
          agg,
          issues,
          summary: `取得 ${agg.totals.appointments}件(業務 ${agg.business_codes.join('/') || 'なし'}${agg.totals.duplicates ? `・重複 ${agg.totals.duplicates}` : ''}${agg.totals.other_date ? `・別日 ${agg.totals.other_date}` : ''})`,
        });
        stats = mergeActivity(stats, date, agg, 'preview', f.name);
        for (const code of agg.business_codes) activeCodes.add(code);
        activeCodes = new Set(activeCodes);
      }
    }
    const importable = planned.some((p) => p.status === 'new' || p.status === 'overwrite' || p.status === 'replace');
    let build: DailySummaryBuildResult | null = null;
    if (importable) {
      const { opRows, activity } = statsToBuildInput(stats);
      build = buildDailySummary({ date, importId: 'preview', opRows, activity, kintai: c?.kintai, operators: deps.operators, companies: deps.companies, policy: deps.policy });
    }
    plans.push({ date, files: planned, build, buildIssues: build?.issues ?? [] });
  }
  return plans;
}

export type RunResult = { fileId: string; result: 'done' | 'skipped' | 'error'; message: string };

/** 計画どおりに順番に取り込む。1ファイル = 1バッチ。上書き・差し替えは includeOverwrite が true のときだけ */
export async function executePlan(
  plans: DatePlan[],
  deps: { operators: Operator[]; companies: Company[]; policy: UnknownBbIdPolicy; userId: string; includeOverwrite: boolean; onProgress?: (r: RunResult) => void },
): Promise<RunResult[]> {
  const results: RunResult[] = [];
  const report = (r: RunResult) => {
    results.push(r);
    deps.onProgress?.(r);
  };
  for (const plan of plans) {
    let ctx: DateContext | null = null;
    for (const p of plan.files) {
      if (p.status === 'skip_same' || p.status === 'error') {
        report({ fileId: p.file.id, result: 'skipped', message: p.status === 'error' ? '読めないため取り込みません' : '同じファイルが取込済み' });
        continue;
      }
      if ((p.status === 'overwrite' || p.status === 'replace') && !deps.includeOverwrite) {
        report({ fileId: p.file.id, result: 'skipped', message: '上書き・差し替えのチェックが無いためスキップ' });
        continue;
      }
      try {
        ctx = await loadDateContext(plan.date);
        const common = { date: plan.date, operators: deps.operators, companies: deps.companies, policy: deps.policy, issues: p.issues, existing: ctx.existing, kintai: ctx.kintai, userId: deps.userId };
        if (p.file.op) {
          const r = await runOpReportImport({ ...common, file: { name: p.file.name, hash: p.file.hash, rowCount: p.file.rowCount, kind: 'op_report', date: plan.date }, rows: p.file.op.rows, stats: ctx.stats, previousHash: ctx.opLogs[0]?.hash ?? null });
          report({ fileId: p.file.id, result: 'done', message: `スタッフ ${r.build.summary.staff.length}行` });
        } else if (p.agg) {
          const r = await runActivityImport({ ...common, file: { name: p.file.name, hash: p.file.hash, rowCount: p.file.rowCount, kind: 'activity', date: plan.date }, agg: p.agg, stats: ctx.stats, previousHash: ctx.actLogs[0]?.hash ?? null });
          report({ fileId: p.file.id, result: 'done', message: `取得 ${p.agg.totals.appointments}件・スタッフ ${r.build.summary.staff.length}行` });
        }
      } catch (err) {
        report({ fileId: p.file.id, result: 'error', message: err instanceof Error ? err.message : '取込に失敗しました' });
      }
    }
  }
  return results;
}
